import { useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, FileText, Layers3, Plus, Receipt, ShoppingCart } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  buildQuotationPresetCounts,
  COMMERCE_QUOTATION_PRESET_QUERY_PARAM,
  filterQuotationsByPreset,
  formatQuotationMoney,
  getQuotationBookingMetaClass,
  getQuotationContractMetaClass,
  getQuotationPresetChipClass,
  getQuotationPresetCountPillClass,
  normalizeQuotationPreset,
  QUOTATION_STATUS_BADGE_CLASS,
  type QuotationListPreset,
} from '@/lib/quotation-sales'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { quotationsApi } from '@/services/quotations'

export default function CommerceQuotationsPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const preset = normalizeQuotationPreset(searchParams.get(COMMERCE_QUOTATION_PRESET_QUERY_PARAM))

  const quotationsQuery = useQuery({
    queryKey: ['commerce', 'quotations', serviceInstanceId],
    queryFn: () => quotationsApi.list({ service_instance_id: serviceInstanceId, page_size: 100 }),
  })

  const quotations = quotationsQuery.data?.results ?? []
  const filteredQuotations = useMemo(() => filterQuotationsByPreset(quotations, preset), [preset, quotations])
  const quotationPresetCounts = useMemo(() => buildQuotationPresetCounts(quotations), [quotations])
  const formatPresetCount = (nextPreset: QuotationListPreset) => (quotationsQuery.isLoading ? '...' : quotationPresetCounts[nextPreset])

  const applyPreset = (nextPreset: QuotationListPreset) => {
    const nextParams = new URLSearchParams(searchParams)
    if (nextPreset === 'all') {
      nextParams.delete(COMMERCE_QUOTATION_PRESET_QUERY_PARAM)
    } else {
      nextParams.set(COMMERCE_QUOTATION_PRESET_QUERY_PARAM, nextPreset)
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Quotations</h1>
          <p className="text-sm text-muted-foreground">Prepare booking quotations with services and inventory particulars.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button asChild variant="outline">
            <Link to={withServiceInstanceId('/services/quotations/new/pos', serviceInstanceId)}>
              <Plus className="mr-2 size-4" />
              POS View
            </Link>
          </Button>
          <Button asChild>
            <Link to={withServiceInstanceId('/services/quotations/new', serviceInstanceId)}>
              <Plus className="mr-2 size-4" />
              Classic View
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Booking Quotations</CardTitle>
          <CardDescription>Accepted quotations can be converted into bookings.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('all', preset === 'all')} onClick={() => applyPreset('all')}>
              <Layers3 className="h-3.5 w-3.5" />
              <span>All</span>
              <span className={getQuotationPresetCountPillClass('all', preset === 'all')}>{formatPresetCount('all')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('open-quotes', preset === 'open-quotes')} onClick={() => applyPreset('open-quotes')}>
              <FileText className="h-3.5 w-3.5" />
              <span>Open Quotes</span>
              <span className={getQuotationPresetCountPillClass('open-quotes', preset === 'open-quotes')}>{formatPresetCount('open-quotes')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('needs-contract', preset === 'needs-contract')} onClick={() => applyPreset('needs-contract')}>
              <Receipt className="h-3.5 w-3.5" />
              <span>Needs Contract</span>
              <span className={getQuotationPresetCountPillClass('needs-contract', preset === 'needs-contract')}>{formatPresetCount('needs-contract')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('converted', preset === 'converted')} onClick={() => applyPreset('converted')}>
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>Converted</span>
              <span className={getQuotationPresetCountPillClass('converted', preset === 'converted')}>{formatPresetCount('converted')}</span>
            </Button>
          </div>
          <div className="mb-3 text-sm text-muted-foreground">Showing {filteredQuotations.length} of {quotations.length} quotations.</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Quotation ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Linked Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Valid Until</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotationsQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>Loading quotations...</TableCell>
                </TableRow>
              ) : filteredQuotations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground">No quotations yet.</TableCell>
                </TableRow>
              ) : filteredQuotations.map((quotation) => (
                <TableRow key={String(quotation.id)}>
                  <TableCell className="font-medium">{quotation.quotation_number}</TableCell>
                  <TableCell>
                    <div className="font-medium text-foreground">{quotation.customer_name || quotation.customer_email || 'Customer'}</div>
                    <div className="text-xs text-muted-foreground">{quotation.contract_document_name || 'Quotation customer'}</div>
                  </TableCell>
                  <TableCell>
                    <div className={`inline-flex rounded-md border px-2.5 py-2 text-sm ${getQuotationBookingMetaClass(quotation.order_reference || quotation.order_id || quotation.order)}`}>
                      {quotation.order_reference || quotation.order_id || quotation.order || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={QUOTATION_STATUS_BADGE_CLASS[quotation.status] ?? 'border-slate-200 text-slate-700'}>
                        {quotation.status}
                      </Badge>
                      <div className={`inline-flex w-fit rounded-md border px-2 py-1 text-xs font-medium ${getQuotationContractMetaClass(Boolean(quotation.effective_contract_document_url || quotation.contract_document_url))}`}>
                        {quotation.effective_contract_document_url || quotation.contract_document_url ? (
                          <>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                            Contract attached
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="mr-1 h-3.5 w-3.5" />
                            Contract missing
                          </>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{formatQuotationMoney(quotation.total)}</TableCell>
                  <TableCell>{quotation.valid_until || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {quotation.status === 'initiated' ? (
                        <Button asChild size="sm" variant="secondary">
                          <Link to={withServiceInstanceId(`/services/quotations/${quotation.id}/edit`, serviceInstanceId)}>
                            Edit
                          </Link>
                        </Button>
                      ) : null}
                      <Button asChild size="sm" variant="outline">
                        <Link to={withServiceInstanceId(`/services/quotations/${quotation.id}`, serviceInstanceId)}>
                          View
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
